/**
 * XC Speed Calculator
 * Ported from extend_spectate_full_features.py
 *
 * Calculates achieved XC (cross-country) speed using MacCready theory
 * and energy compensation (TEC altitude).
 */

const XC_SPEED_HISTORY_TIME = 5; // seconds for current average
const XC_SPEED_WEIGHT_DECAY = 0.1; // weight decay factor

export class XCSpeedCalculator {
  constructor() {
    this.xcSpeedHistory = new Map(); // Map<aircraftId, Array<{time, speed}>>
    this.startHeights = new Map(); // Map<aircraftId, startHeight_m>
  }

  /**
   * Set the start height for an aircraft
   * @param {string} aircraftId - Aircraft identifier
   * @param {number} startHeight_m - Start height in meters
   */
  setStartHeight(aircraftId, startHeight_m) {
    this.startHeights.set(aircraftId, startHeight_m);
  }

  /**
   * Calculate XC speed using MacCready theory and TEC altitude
   * Based on Python's calculate_xc_speed method
   *
   * @param {string} aircraftId - Aircraft identifier
   * @param {number} currentTec_m - Current TEC altitude in meters
   * @param {number} elapsedTime_s - Elapsed time since start in seconds
   * @param {number} distance_km - Distance covered in kilometers
   * @param {number} mcSpeedKmh - MacCready speed in km/h
   * @param {number} mcSinkMs - MacCready sink rate in m/s (negative)
   * @param {number} mcSetting - MacCready setting in m/s
   * @returns {number} XC speed in km/h
   */
  calculateXcSpeed(aircraftId, currentTec_m, elapsedTime_s, distance_km, mcSpeedKmh, mcSinkMs, mcSetting) {
    const currentTime = Date.now() / 1000; // Convert to seconds
    const elapsedTime_hours = elapsedTime_s / 3600;

    if (elapsedTime_hours <= 0) {
      return 0;
    }

    const startHeight_m = this.startHeights.get(aircraftId) || 0;
    const deltaAlt_km = (currentTec_m - startHeight_m) / 1000.0;

    // Convert sink rate to km/h
    const mcSinkKmh = mcSinkMs * 3.6;

    // Calculate glide ratio at MacCready speed
    const mcGlideRatio = Math.abs(mcSinkKmh) > 0 ? mcSpeedKmh / Math.abs(mcSinkKmh) : 0;

    // Calculate equivalent time penalty for altitude loss
    const equivalentTime = Math.abs(mcSinkKmh) > 0 ? deltaAlt_km / Math.abs(mcSinkKmh) : 0;

    // Calculate average speed accounting for climb time
    const mcSettingKmh = mcSetting * 3.6;
    const mcAvgSpeed = mcSettingKmh !== 0 ? mcSpeedKmh / (1 + (Math.abs(mcSinkKmh) / mcSettingKmh)) : 0;

    // XC speed formula: (altitude credit + distance - time penalty) / elapsed time
    // altitude credit = delta_alt * glide_ratio
    // time penalty = equivalent_time * avg_speed
    const altitudeCredit = deltaAlt_km * mcGlideRatio;
    const timePenalty = equivalentTime * mcAvgSpeed;
    const numerator = altitudeCredit + distance_km - timePenalty;
    const currentXcSpeed = elapsedTime_hours > 0 ? numerator / elapsedTime_hours : 0;

    // Store in history for smoothing
    if (!this.xcSpeedHistory.has(aircraftId)) {
      this.xcSpeedHistory.set(aircraftId, []);
    }

    const history = this.xcSpeedHistory.get(aircraftId);
    history.push({ time: currentTime, speed: currentXcSpeed });

    // Remove old entries (older than XC_SPEED_HISTORY_TIME seconds)
    while (history.length > 0 && (currentTime - history[0].time > XC_SPEED_HISTORY_TIME)) {
      history.shift();
    }

    // Calculate weighted average with exponential decay
    if (history.length > 0) {
      let totalWeight = 0;
      let weightedSum = 0;

      for (const entry of history) {
        const age = currentTime - entry.time;
        const weight = Math.exp(-age);
        weightedSum += entry.speed * weight;
        totalWeight += weight;
      }

      let avgXcSpeed = totalWeight > 0 ? weightedSum / totalWeight : currentXcSpeed;

      // Additional smoothing with previous speed
      if (history.length > 1) {
        const prevSpeed = history[history.length - 2].speed;
        avgXcSpeed = 0.7 * avgXcSpeed + 0.3 * prevSpeed;
      }

      return Math.round(avgXcSpeed * 10) / 10; // Round to 1 decimal
    }

    return Math.round(currentXcSpeed * 10) / 10;
  }

  /**
   * Calculate XC speed with netto compensation
   * Based on Python's calculate_xc_speed_netto method
   *
   * @param {string} aircraftId - Aircraft identifier
   * @param {number} currentTec_m - Current TEC altitude in meters
   * @param {number} elapsedTime_s - Elapsed time since start in seconds
   * @param {number} distance_km - Distance covered in kilometers
   * @param {number} mcSpeedKmh - MacCready speed in km/h
   * @param {number} mcSinkMs - MacCready sink rate in m/s (negative)
   * @param {number} mcSetting - MacCready setting in m/s
   * @param {number} nettoSetting - Netto setting in m/s
   * @returns {number} XC speed with netto in km/h
   */
  calculateXcSpeedNetto(aircraftId, currentTec_m, elapsedTime_s, distance_km, mcSpeedKmh, mcSinkMs, mcSetting, nettoSetting) {
    const elapsedTime_hours = elapsedTime_s / 3600;

    if (elapsedTime_hours <= 0) {
      return 0;
    }

    const startHeight_m = this.startHeights.get(aircraftId) || 0;
    const deltaAlt_km = (currentTec_m - startHeight_m) / 1000.0;

    const mcSinkKmh = mcSinkMs * 3.6;
    const nettoKmh = nettoSetting * 3.6;

    // Apply netto to the glide ratio (cruise credit)
    let effectiveSinkKmh = mcSinkKmh + nettoKmh;
    if (Math.abs(effectiveSinkKmh) < 0.1) {
      effectiveSinkKmh = effectiveSinkKmh >= 0 ? 0.1 : -0.1;
    }

    const mcGlideRatio = mcSpeedKmh / Math.abs(effectiveSinkKmh);

    // Time penalties remain based on still-air sink
    const equivalentTime = Math.abs(mcSinkKmh) > 0 ? deltaAlt_km / Math.abs(mcSinkKmh) : 0;

    const mcSettingKmh = mcSetting * 3.6;
    const mcAvgSpeed = mcSettingKmh !== 0 ? mcSpeedKmh / (1 + (Math.abs(mcSinkKmh) / mcSettingKmh)) : 0;

    const numerator = (deltaAlt_km * mcGlideRatio + distance_km) - (equivalentTime * mcAvgSpeed);
    const currentXcSpeedNetto = elapsedTime_hours > 0 ? numerator / elapsedTime_hours : 0;

    return Math.round(currentXcSpeedNetto * 10) / 10;
  }

  /**
   * Clear history for an aircraft
   * @param {string} aircraftId - Aircraft identifier
   */
  clearHistory(aircraftId) {
    this.xcSpeedHistory.delete(aircraftId);
    this.startHeights.delete(aircraftId);
  }

  /**
   * Clear all history
   */
  clearAllHistory() {
    this.xcSpeedHistory.clear();
    this.startHeights.clear();
  }
}

// Create singleton instance
export const xcSpeedCalculator = new XCSpeedCalculator();
